<template>
  <div class="airflow-documentation-tab">
    <div class="text-h6 q-mb-md">Airflow Distribution & Configuration</div>

    <!--
      NOTE: This is a complex tab requiring the useAirflowDiagram composable
      to handle zone allocation calculations, VAV flow tracking, and Sankey diagram generation.

      Current implementation passes complex logic to parent via props/emits.
      Future: Extract logic to useAirflowDiagram composable
    -->

    <div class="row q-col-gutter-md">
      <!-- Left Column: Flow Rates and Configuration -->
      <div class="col-12 col-md-4">
        <q-card flat bordered class="full-height">
          <q-card-section>
            <div class="text-subtitle2 q-mb-sm">AHU Source</div>

            <!-- AHU Display -->
            <q-card flat bordered class="q-mb-md bg-green-1">
              <q-item>
                <q-item-section avatar>
                  <q-icon name="air" color="green" size="md" />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-weight-bold">{{ equipmentName || 'AHU' }}</q-item-label>
                  <q-item-label caption>Primary Air Handler</q-item-label>
                </q-item-section>
              </q-item>
            </q-card>

            <q-separator class="q-my-md" />

            <div class="text-subtitle2 q-mb-sm">Flow Rates</div>

            <q-input
              :model-value="modelValue.airflow_data?.supply_flow"
              label="Supply Air Flow (l/s)"
              type="number"
              outlined
              dense
              class="q-mb-sm"
              @update:model-value="updateField('supply_flow', Number($event))"
            >
              <template #prepend>
                <q-icon name="arrow_forward" color="blue" />
              </template>
            </q-input>

            <q-input
              :model-value="modelValue.airflow_data?.return_flow"
              label="Return Air Flow (l/s)"
              type="number"
              outlined
              dense
              class="q-mb-sm"
              @update:model-value="updateField('return_flow', Number($event))"
            >
              <template #prepend>
                <q-icon name="arrow_back" color="orange" />
              </template>
            </q-input>

            <q-input
              :model-value="modelValue.airflow_data?.outdoor_air_flow"
              label="Outdoor Air Flow (l/s)"
              type="number"
              outlined
              dense
              class="q-mb-sm"
              @update:model-value="updateField('outdoor_air_flow', Number($event))"
            >
              <template #prepend>
                <q-icon name="eco" color="green" />
              </template>
            </q-input>

            <q-input
              :model-value="modelValue.airflow_data?.static_pressure"
              label="Static Pressure (inH2O)"
              type="number"
              step="0.1"
              outlined
              dense
              class="q-mb-sm"
              @update:model-value="updateField('static_pressure', Number($event))"
            >
              <template #prepend>
                <q-icon name="compress" />
              </template>
            </q-input>

            <q-separator class="q-my-md" />

            <!-- Zone Sections Configuration -->
            <div class="text-subtitle2 q-mb-sm">Zone Sections Configuration</div>
            <div class="text-caption text-grey-7 q-mb-md">
              Zone sections are configured in the parent AHU equipment.
              Full zone section management requires the useAirflowDiagram composable.
            </div>

            <q-separator class="q-my-md" />

            <!-- Connected Equipment -->
            <div class="text-subtitle2 q-mb-sm">Connected Equipment</div>
            <div class="text-caption text-grey-7 q-mb-md">
              VAV connections and space management requires the useAirflowDiagram composable.
              This will be implemented in a future refactoring phase.
            </div>
          </q-card-section>
        </q-card>
      </div>

      <!-- Right Column: Sankey Diagram Visualization -->
      <div class="col-12 col-md-8">
        <q-card flat bordered class="full-height">
          <q-card-section class="q-pa-sm">
            <div class="row items-center q-mb-sm">
              <div class="text-subtitle2">Airflow Distribution Diagram</div>
              <q-space />
              <q-btn
                flat
                round
                icon="refresh"
                size="sm"
                @click="emit('refresh-diagram')"
              >
                <q-tooltip>Refresh Diagram</q-tooltip>
              </q-btn>
            </div>

            <div
              id="airflow-sankey-container"
              class="airflow-diagram-container"
              :style="{ height: '500px', width: '100%' }"
            >
              <div class="text-center q-pa-xl text-grey-6">
                <q-icon name="air" size="64px" color="grey-4" />
                <div class="text-h6 q-mt-md">Airflow Visualization</div>
                <div class="text-body2 q-mt-sm">
                  Sankey diagram generation requires useAirflowDiagram composable
                </div>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * Airflow Documentation Tab Component
 *
 * Displays interface for documenting airflow distribution and configuration
 *
 * - Zone allocation calculations
 * - VAV flow tracking
 * - Sankey diagram generation
 * - Space connection management
 */

  const props = defineProps({
    modelValue: {
      type: Object,
      required: true,
      default: () => ({
        airflow_data: {
          supply_flow: null,
          return_flow: null,
          outdoor_air_flow: null,
          static_pressure: null,
        },
      }),
    },
    equipmentName: {
      type: String,
      default: 'AHU',
    },
  });

  const emit = defineEmits([
    'update:airflow-field',
    'refresh-diagram',
  ]);

  function updateField(field, value) {
    emit('update:airflow-field', { field, value });
  }
</script>

<style scoped>
.airflow-documentation-tab {
  height: 100%;
}

.airflow-diagram-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}
</style>
